import { createWithEqualityFn } from 'zustand/traditional';

import {
  ApplicantColleges,
  FormNames,
  Roles,
  FormActionsStatus,
  RoleEnum,
  FormRequest,
  PdfResponseWithForm,
  ApproveRecommendationRequest,
  SuccessResult,
  PdfResponse,
  FormSubmissionRequest,
  CounselorForms,
} from '@youscience/commonapp-service-common';

import { commonAppService } from '@services/commonApp.service';

import { ALL_TYPES_VALUE } from '@constants';
import { SORT_OPTIONS } from '@constants/recommenderWorkspace';

import { ApplicantFormRequests } from '@interfaces/recommenderWorkspace';
import { Order } from '@interfaces';

import { notify } from '@store/NotificationStore';

import { FormSubmissionIds } from '@youscience/commonapp-service-common/dist/cjs/models/youScience/commonRequests';
import { onSubmitSuccessNotification } from '@pages/StudentDetailsPage/components/common/formUtils';

const FORM_NAMES_TO_URL_MAP = {
  [FormNames.SECONDARY_REPORT]: CounselorForms.SECONDARY_REPORT,
  [FormNames.MIDYEAR_REPORT]: CounselorForms.MIDYEAR_REPORT,
  [FormNames.FINAL_REPORT]: CounselorForms.FINAL_REPORT,
  [FormNames.RECOMMENDATION]: CounselorForms.RECOMMENDATION,
  [FormNames.EARLY_DECISION]: CounselorForms.EARLY_DECISION,
  [FormNames.EARLY_DECISION2]: CounselorForms.EARLY_DECISION2,
  [FormNames.FEE_WAIVER]: CounselorForms.FEE_WAIVER,
  [FormNames.OPTIONAL_REPORT]: CounselorForms.OPTIONAL_REPORT,
  [FormNames.OPTIONAL_REPORT2]: CounselorForms.OPTIONAL_REPORT2,
  [FormNames.TEACHER_EVALUATION]: 'recommendation',
};

const getUpdatedForms = <T extends Partial<FormSubmissionIds>>(
  applicant: ApplicantColleges,
  name: FormNames,
  form: T,
): T | T[] => {
  if (name === FormNames.TEACHER_EVALUATION) {
    const teacherForms =
      applicant.forms[FormNames.TEACHER_EVALUATION]?.filter((f) => f.recommenderId !== form.recommenderId) || [];

    return [...teacherForms, form] as T[];
  } else {
    return form;
  }
};

export type ApplicationStatusFilter = typeof ALL_TYPES_VALUE | FormActionsStatus;

export interface TableSearchParams {
  applicationStatusFilter: ApplicationStatusFilter;
  searchValue: string;
  sortOrder: Order;
  sortOrderBy: keyof ApplicantFormRequests;
}

export interface RecommenderWorkspaceStore {
  activeTabs: {
    activePageTab: number;
    activeApplicationForm: number;
  };
  setActiveTabs: (data: Partial<{ activePageTab: number; activeApplicationForm: number }>) => void;
  isLoadingApplicantFormsAvailable: boolean;
  isLoadingApplicant: boolean;
  isLoadingPreview: boolean;
  isLoadingTeacherEvaluation: boolean;
  isSavingForm: boolean;
  applicantFormsAvailable: FormRequest;
  tableSearchParams: TableSearchParams;
  applicant: ApplicantColleges;
  previewPdfURL: string | undefined;
  isError: boolean;
  setTableSearchParams: (params: Partial<TableSearchParams>) => void;
  getApplicantById: (applicantId: number) => Promise<ApplicantColleges>;
  getApplicantFormsAvailable: (role: RoleEnum) => Promise<void>;
  saveForm: <T>(data: Partial<T>, name: FormNames) => void;
  previewForm: <T extends FormSubmissionRequest>(data: Partial<T>, name: FormNames) => void;
  submitForm: <T extends FormSubmissionRequest>(data: Partial<T>, name: FormNames) => void;
  unsubmitForm: <T extends FormSubmissionRequest>(data: Partial<T>, name: FormNames) => void;
  getSubmittedForm: <T extends FormSubmissionRequest>(data: Partial<T>, name: FormNames) => void;
  getTeacherPreviewPdf: (applicantId: number, recommenderId: number) => void;
  rejectTeacherEvaluation: (applicantId: number, recommenderId: number) => Promise<SuccessResult>;
  approveTeacherEvaluation: (
    applicantId: number,
    recommenderId: number,
    data: Partial<ApproveRecommendationRequest>,
  ) => Promise<SuccessResult>;
  getSubmittedReportPdf: (
    applicantId: number,
    recommenderId: number,
    formId: CounselorForms | 'recommendation',
    memberId?: number,
    role?: Roles,
  ) => Promise<void>;
}

const initialTableSearchParams: Partial<TableSearchParams> = {
  applicationStatusFilter: ALL_TYPES_VALUE,
  sortOrder: Order.asc,
  sortOrderBy: 'deadline',
};

const initialState = {
  activeTabs: {
    activePageTab: 0,
    activeApplicationForm: 0,
  },
  isLoadingApplicantFormsAvailable: false,
  isSavingForm: false,
  applicantFormsAvailable: {},
  tableSearchParams: initialTableSearchParams,
} as RecommenderWorkspaceStore;

export const useRecommenderWorkspaceStore = createWithEqualityFn<RecommenderWorkspaceStore>()(
  (set, get) => ({
    ...initialState,

    setActiveTabs: (data: Partial<{ activePageTab: number; activeApplicationForm: number }>) => {
      set({
        activeTabs: { ...get().activeTabs, ...data },
      });
    },

    setTableSearchParams: (params: Partial<TableSearchParams>) => {
      set({
        tableSearchParams: { ...get().tableSearchParams, ...params },
      });
    },

    getApplicantById: async (applicantId: number) => {
      set({
        isLoadingApplicant: true,
        applicant: undefined,
      });

      const applicant = await commonAppService.get<ApplicantColleges>(`/applicant/${applicantId}`);

      set({
        isLoadingApplicant: false,
        applicant: applicant.data,
      });

      return applicant.data;
    },

    getApplicantFormsAvailable: async (role: RoleEnum) => {
      const {
        tableSearchParams: { applicationStatusFilter, searchValue, sortOrderBy, sortOrder },
      } = get();

      set({
        isLoadingApplicantFormsAvailable: true,
        applicantFormsAvailable: undefined,
      });

      try {
        const params: Record<string, unknown> = {
          status: applicationStatusFilter,
          legalName: searchValue,
          sort: SORT_OPTIONS[sortOrderBy as string][sortOrder],
        };

        if (applicationStatusFilter === ALL_TYPES_VALUE) {
          delete params.status;
        }

        const applicantFormResponse = await commonAppService.getAsync<FormRequest>(`recommender/applicant/forms`, {
          params: { personaType: role || Roles.COUNSELOR, ...params },
        });

        set({
          isLoadingApplicantFormsAvailable: false,
          applicantFormsAvailable: applicantFormResponse,
        });
      } catch (error) {
        set({
          isLoadingApplicantFormsAvailable: false,
        });

        throw error;
      }
    },

    getSubmittedReportPdf: async (
      applicantId: number,
      recommenderId: number,
      formId: CounselorForms | 'recommendation',
      memberId = NaN,
      role = Roles.COUNSELOR,
    ) => {
      set({
        isLoadingPreview: true,
        isError: false,
        previewPdfURL: undefined,
      });

      const rolePath = role.toLowerCase();
      const memberIdPath = isNaN(memberId) ? '' : `/${memberId}`;
      try {
        const pdfObject = await commonAppService.getAsync<PdfResponse>(
          `${rolePath}/${recommenderId}/${applicantId}${memberIdPath}/${formId}/submitted-pdf`,
        );

        set({
          isError: false,
          isLoadingPreview: false,
          previewPdfURL: pdfObject.filePath,
        });
      } catch (error) {
        set({
          isError: true,
          isLoadingPreview: false,
        });
      }
    },

    saveForm: async <T extends FormSubmissionRequest>(data: Partial<T>, name: FormNames) => {
      set({
        isSavingForm: true,
        isError: false,
      });

      const url = `${name === FormNames.TEACHER_EVALUATION ? 'teacher' : 'counselor'}/form/${FORM_NAMES_TO_URL_MAP[name]}`;

      try {
        const form = await commonAppService.postAsync<T, T>(url, data as T);

        const currentApplicant = get().applicant;

        set({
          isSavingForm: false,
          isError: false,
          applicant: {
            ...currentApplicant,
            forms: {
              ...currentApplicant.forms,
              [name]: getUpdatedForms(currentApplicant, name, form),
            },
          },
        });
      } catch (error) {
        set({
          isError: true,
          isSavingForm: false,
        });
      }
    },

    previewForm: async <T extends FormSubmissionRequest>(data: Partial<T>, name: FormNames) => {
      set({
        isLoadingPreview: true,
        isError: false,
        previewPdfURL: undefined,
      });

      const url = `${name === FormNames.TEACHER_EVALUATION ? 'teacher' : 'counselor'}/preview/${FORM_NAMES_TO_URL_MAP[name]}`;

      try {
        const preview = await commonAppService.postAsync<PdfResponseWithForm<T>, T>(url, data as T);

        const currentApplicant = get().applicant;

        set({
          applicant: {
            ...currentApplicant,
            forms: {
              ...currentApplicant.forms,
              [name]: getUpdatedForms(currentApplicant, name, preview.form),
            },
          },
          isError: false,
          isLoadingPreview: false,
          previewPdfURL: preview.filePath,
        });
      } catch (error) {
        set({
          isError: true,
          isLoadingPreview: false,
        });
      }
    },

    submitForm: async <T extends FormSubmissionRequest>(data: Partial<T>, name: FormNames) => {
      set({
        isSavingForm: true,
        isError: false,
      });

      const url = `${name === FormNames.TEACHER_EVALUATION ? 'teacher' : 'counselor'}/submit/${FORM_NAMES_TO_URL_MAP[name]}`;

      try {
        await commonAppService.postAsync<T, T>(url, data as T);

        const applicant = await get().getApplicantById(data.applicantId!);

        set({
          isSavingForm: false,
          isError: false,
          applicant,
        });
      } catch (error) {
        set({
          isSavingForm: false,
          isError: true,
        });
      }

      onSubmitSuccessNotification(name);
    },

    getSubmittedForm: async <T extends FormSubmissionRequest>(data: Partial<T>, name: FormNames) => {
      set({
        isLoadingPreview: true,
        isError: false,
        previewPdfURL: undefined,
      });

      const { applicantId, recommenderId, memberId } = data;

      const memberPath = memberId && name === FormNames.TEACHER_EVALUATION ? `/${memberId}` : '';

      const url = `${name === FormNames.TEACHER_EVALUATION ? 'teacher' : 'counselor'}/${recommenderId}/${applicantId}${memberPath}/${FORM_NAMES_TO_URL_MAP[name]}/submitted-pdf`;

      try {
        const pdfObject = await commonAppService.getAsync<PdfResponse>(url);

        set({
          isError: false,
          isLoadingPreview: false,
          previewPdfURL: pdfObject.filePath,
        });
      } catch (error) {
        set({
          isError: true,
          isLoadingPreview: false,
        });
      }
    },

    unsubmitForm: async <T extends FormSubmissionRequest>(data: Partial<T>, name: FormNames) => {
      const { applicantId, recommenderId } = data;
      const url = `${name === FormNames.TEACHER_EVALUATION ? 'teacher' : 'counselor'}/delete/${FORM_NAMES_TO_URL_MAP[name]}`;

      set({
        isSavingForm: true,
        isError: false,
      });

      try {
        const applicant = await commonAppService.deleteAsync<ApplicantColleges>(url, {
          data: {
            applicantId,
            recommenderId,
          },
        });

        set({
          isSavingForm: false,
          isError: false,
          applicant,
        });
      } catch (error) {
        set({
          isError: true,
          isSavingForm: false,
        });
      }
    },

    getTeacherPreviewPdf: async (applicantId: number, recommenderId: number) => {
      set({
        isLoadingPreview: true,
      });

      const pdfResponse = await commonAppService.getAsync<PdfResponse>(
        `/teacher/preview/recommendation/${applicantId}/${recommenderId}`,
      );

      set({
        previewPdfURL: pdfResponse.filePath,
        isLoadingPreview: false,
      });
    },

    rejectTeacherEvaluation: async (applicantId: number, recommenderId: number) => {
      const { getApplicantById } = get();

      set({
        isLoadingTeacherEvaluation: true,
      });

      const successResult = await commonAppService.postAsync<SuccessResult, unknown>(
        `/counselor/teacher-recommendation/reject/${applicantId}/${recommenderId}`,
      );

      await getApplicantById(applicantId);

      set({
        isLoadingTeacherEvaluation: false,
      });

      return successResult;
    },

    approveTeacherEvaluation: async (
      applicantId: number,
      recommenderId: number,
      data: Partial<ApproveRecommendationRequest>,
    ) => {
      const { getApplicantById } = get();

      set({
        isLoadingTeacherEvaluation: true,
      });

      try {
        const successResult = await commonAppService.postAsync<SuccessResult, unknown>(
          `/counselor/teacher-recommendation/approve/${applicantId}/${recommenderId}`,
          data,
        );

        await getApplicantById(applicantId);

        notify({
          title: 'Evaluation submitted.',
          message: 'Teacher evaluation submitted successfully.',
          severity: 'success',
        });

        set({
          isLoadingTeacherEvaluation: false,
        });

        return successResult;
      } catch (error: unknown) {
        set({
          isLoadingTeacherEvaluation: false,
        });

        return {
          message: (error as Error)?.message,
          success: false,
        } as SuccessResult;
      }
    },
  }),
  Object.is,
);
